/**
 * One settings input with its label, hint and error (T1b).
 *
 * The label is a real `<label htmlFor>`, the hint and the error are tied to the
 * input through `aria-describedby`, and `aria-invalid` marks a refused value —
 * so a screen reader hears what the field is, what it is for and what is wrong
 * with it, and none of that depends on colour.
 *
 * READ-ONLY WHILE SAVING, NOT DISABLED. A disabled input drops the focus the
 * person just pressed Enter in; read-only keeps it there and still stops an edit
 * from landing in the middle of a save whose answer replaces the form.
 */
import React from "react";

const LABEL = "block text-[12.5px] font-medium text-foreground";
const OPTIONAL = "ml-1.5 text-[11.5px] font-normal text-muted-foreground";
const INPUT =
  "mt-1.5 block h-9 w-full min-w-0 rounded-[8px] border bg-background px-2.5 " +
  "text-base text-foreground placeholder:text-muted-foreground " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "read-only:opacity-70 md:text-[13px]";
const INPUT_BORDER = "border-input";
const INPUT_INVALID = "border-destructive";
const HINT = "m-0 mt-1 text-[11.5px] leading-snug text-muted-foreground";
// Foreground text with a destructive MARKER, not destructive text: the light
// `--destructive` measures under the 4.5:1 a sentence needs (TerminationForm).
const ERROR =
  "m-0 mt-1.5 border-l-2 border-destructive pl-2 text-[11.5px] leading-snug text-foreground";

export default function EmailSettingsField({
  id,
  label,
  optional = false,
  hint = "",
  type = "text",
  value,
  error = "",
  maxLength,
  placeholder,
  readOnly = false,
  onChange,
}) {
  const hintId = hint ? `${id}-hint` : null;
  const errorId = error ? `${id}-error` : null;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;
  const isEmail = type === "email";

  return (
    <div className="min-w-0">
      <label htmlFor={id} className={LABEL}>
        {label}
        {optional ? <span className={OPTIONAL}>(optional)</span> : null}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
        readOnly={readOnly}
        autoComplete="off"
        spellCheck={false}
        autoCapitalize={isEmail ? "none" : undefined}
        inputMode={isEmail ? "email" : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        className={`${INPUT} ${error ? INPUT_INVALID : INPUT_BORDER}`}
      />
      {error ? (
        <p id={errorId} className={ERROR}>
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className={HINT}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
